import {
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faQuestionCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';

import type {
  ZuubCoverage,
  ZuubPatientData,
  ZuubPatientDetailDataV3,
  ZuubPatientListItemV3,
  ZuubPlanListDataV3,
  ZuubService,
} from '../../@types/zuub/contracts';
import type { Appointment } from '../../react-query/useAppointments';

/**
 * Determines if the View Benefits button should be disabled based on patient data
 *
 * @param zuubPatient - The Zuub patient data
 * @param patientId - The patient ID
 * @returns True if the button should be disabled, false otherwise
 */
export const shouldDisableViewBenefitsButton = (
  zuubPatient: ZuubPatientData | undefined,
  patientId: string | undefined,
): boolean => {
  if (!patientId) return true;

  const detailedData = zuubPatient?.detailedData?.[0];
  const verificationStatus = detailedData?.verificationStatus?.toLowerCase();
  const insuranceStatus = detailedData?.insuranceStatus?.toLowerCase();
  const payerName = detailedData?.payerName?.toLowerCase();

  // Check for conditions that should disable the button
  const isPayerNameMissing = !detailedData?.payerName;
  const isPayerError =
    insuranceStatus === 'inactive' || payerName === 'unknown payer';
  const isPatientNotFound = !zuubPatient;
  const isPatientNotFoundStatus = verificationStatus === 'patient not found';
  const isUnknownStatus = verificationStatus === 'unknown';

  return (
    isPayerNameMissing ||
    isPayerError ||
    isPatientNotFound ||
    isPatientNotFoundStatus ||
    isUnknownStatus
  );
};

/**
 * Sorts appointments based on the specified field and direction
 *
 * @param appointments - Array of appointments to sort
 * @param sortField - The field to sort by ('flag', 'patient', 'appointment', 'procedureCodes')
 * @param sortDirection - The sort direction ('asc' or 'desc')
 * @param zuubDataMap - Optional map of unifiedAppointmentId to ZuubPatientData for flag sorting
 * @returns Sorted array of appointments
 */
export const sortAppointments = (
  appointments: Appointment[],
  sortField: string,
  sortDirection: 'asc' | 'desc',
  zuubDataMap?: Map<string, ZuubPatientData>,
): Appointment[] => {
  if (!appointments) return [];

  return appointments.sort((a, b) => {
    if (sortField === 'flag') {
      if (!zuubDataMap) return 0;

      const aZuubPatient = zuubDataMap.get(a.appointment.unifiedId);
      const bZuubPatient = zuubDataMap.get(b.appointment.unifiedId);

      const hasFlagA =
        aZuubPatient?.detailedData?.some(
          (data) => data.needsPayerPortalConnection,
        ) ?? false;
      const hasFlagB =
        bZuubPatient?.detailedData?.some(
          (data) => data.needsPayerPortalConnection,
        ) ?? false;

      // In ascending order: items with flags first (true = -1, false = 1)
      // In descending order: items with flags last (true = 1, false = -1)
      if (hasFlagA !== hasFlagB) {
        return sortDirection === 'asc'
          ? hasFlagA
            ? -1
            : 1
          : hasFlagA
            ? 1
            : -1;
      }

      // If both have the same flag status, maintain original order
      return 0;
    }
    if (sortField === 'patient') {
      const aName = a.patient.firstName + ' ' + a.patient.lastName;
      const bName = b.patient.firstName + ' ' + b.patient.lastName;
      return sortDirection === 'asc'
        ? aName.localeCompare(bName)
        : bName.localeCompare(aName);
    }
    if (sortField === 'appointment') {
      const aDate = new Date(a.appointment.start).getTime();
      const bDate = new Date(b.appointment.start).getTime();
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }
    if (sortField === 'procedureCodes') {
      // For now, procedure codes are empty, so no sorting needed
      return 0;
    }
    return 0;
  });
};

/**
 * Gets the display configuration for verification status
 *
 * @param status - The verification status string
 * @returns Object with status text, icon, and icon color
 */
export const getVerificationStatusDisplay = (status: string) => {
  switch (status.toLowerCase()) {
    case 'verified':
      return {
        status: 'Verified',
        icon: faCheckCircle,
        iconColor: 'vai-text-green-600',
      };
    case 'failed':
      return {
        status: 'Failed',
        icon: faTimesCircle,
        iconColor: 'vai-text-red-600',
      };
    case 'needs attention':
    case 'needs_attention':
      return {
        status: 'Needs attention',
        icon: faExclamationTriangle,
        iconColor: 'vai-text-amber-500',
      };
    case 'pending':
      return {
        status: 'Pending',
        icon: faClock,
      };
    default:
      return {
        status: status || 'Unknown',
        icon: faQuestionCircle,
        iconColor: 'vai-text-gray-500',
      };
  }
};

/**
 * Gets the insurance status configuration for StatusChip
 *
 * @param status - The insurance status string
 * @returns Object with variant and optional icon
 */
export const getInsuranceStatusConfig = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return {
        variant: 'success' as const,
        icon: undefined,
      };
    case 'inactive':
      return {
        variant: 'error' as const,
        icon: undefined,
      };
    case 'pending':
      return {
        variant: 'default' as const,
        icon: faClock,
      };
    case 'unknown':
      return {
        variant: 'warning' as const,
        icon: undefined,
      };

    default:
      return {
        variant: 'default' as const,
        icon: undefined,
      };
  }
};

/**
 * Gets the verification status for an appointment
 * Checks Zuub data first, then falls back to a default status
 *
 * @param appointment - The appointment to get verification status for
 * @param zuubDataMap - Map of unifiedAppointmentId to ZuubPatientData
 * @returns The verification status string (lowercase)
 */
export const getAppointmentVerificationStatus = (
  appointment: Appointment,
  zuubDataMap: Map<string, ZuubPatientData>,
): string => {
  const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
  const detailedData = zuubPatient?.detailedData?.[0];
  const verificationStatus = detailedData?.verificationStatus
    ? getVerificationStatusDisplay(detailedData.verificationStatus)
    : { status: 'Unknown' }; // Default status when no Zuub data
  return verificationStatus.status.toLowerCase();
};

/**
 * Gets the insurance status for an appointment
 * Checks Zuub data first, then falls back to "unknown"
 *
 * @param appointment - The appointment to get insurance status for
 * @param zuubDataMap - Map of unifiedAppointmentId to ZuubPatientData
 * @returns The insurance status string (lowercase)
 */
export const getAppointmentInsuranceStatus = (
  appointment: Appointment,
  zuubDataMap: Map<string, ZuubPatientData>,
): string => {
  const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
  const detailedData = zuubPatient?.detailedData?.[0];
  const status = detailedData?.insuranceStatus || 'unknown';
  return status.toLowerCase();
};

/**
 * Extracts unique payer names from Zuub patient data
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @returns Array of unique payer names
 */
export const getUniquePayerNames = (
  zuubPatientData: ZuubPatientData | null,
): string[] => {
  if (!zuubPatientData?.detailedData) return [];

  const payerNames = zuubPatientData.detailedData
    .map((plan) => plan.payerName)
    .filter((name): name is string => !!name);

  return [...new Set(payerNames)]; // Remove duplicates
};

/**
 * Extracts unique plan types from Zuub patient data
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @returns Array of unique plan types
 */
export const getUniquePlanTypes = (
  zuubPatientData: ZuubPatientData | null,
): string[] => {
  if (!zuubPatientData?.detailedData) return [];

  const planTypes = zuubPatientData.detailedData
    .map((plan) => plan.plan?.planType)
    .filter((type): type is string => !!type);

  return [...new Set(planTypes)]; // Remove duplicates
};

/**
 * Gets plan types available for a specific payer name
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @param payerName - The payer name to filter by
 * @returns Array of plan types available for the specified payer
 */
export const getPlanTypesForPayer = (
  zuubPatientData: ZuubPatientData | null,
  payerName: string,
): string[] => {
  if (!zuubPatientData?.detailedData || !payerName) return [];

  const planTypes = zuubPatientData.detailedData
    .filter((plan) => plan.payerName === payerName)
    .map((plan) => plan.plan?.planType)
    .filter((type): type is string => !!type);

  return [...new Set(planTypes)]; // Remove duplicates
};

/**
 * Finds a plan by payer name and returns its status
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @param payerName - The payer name to search for
 * @returns The plan status (active/inactive) or null if not found
 */
export const getPlanStatusByPayerName = (
  zuubPatientData: ZuubPatientData | null,
  payerName: string,
): string | null => {
  if (!zuubPatientData?.detailedData) return null;

  const plan = zuubPatientData.detailedData.find(
    (planData) => planData.payerName === payerName,
  );

  return plan?.plan?.status || null;
};

/**
 * Finds a plan by plan type and returns its status
 *
 * @param zuubPatientData - The Zuub patient data containing detailed plan information
 * @param planType - The plan type to search for
 * @returns The plan status (active/inactive) or null if not found
 */
export const getPlanStatusByPlanType = (
  zuubPatientData: ZuubPatientData | null,
  planType: string,
): string | null => {
  if (!zuubPatientData?.detailedData) return null;

  const plan = zuubPatientData.detailedData.find(
    (planData) => planData.plan?.planType === planType,
  );

  return plan?.plan?.status || null;
};

/**
 * Checks if a plan status indicates the plan is active
 *
 * @param status - The plan status string
 * @returns True if the plan is active, false otherwise
 */
export const isPlanActive = (status: string | null): boolean => {
  return status?.toLowerCase() === 'active';
};

/**
 * Gets benefits data for a specific coverage category
 */
export const getBenefitsByCategory = (
  zuubPatientData: ZuubPatientData | null,
  categoryName: string,
): { remaining: number; total: number } => {
  if (!zuubPatientData?.detailedData?.[0]) {
    return { remaining: 0, total: 0 };
  }

  const plan = zuubPatientData.detailedData[0];
  const category = plan.coverage?.find(
    (coverage) =>
      coverage.categoryName.toLowerCase() === categoryName.toLowerCase(),
  );

  if (!category) {
    return { remaining: 0, total: 0 };
  }

  // Use the overall benefit summary for total benefits
  // and calculate remaining benefits from individual services in this category
  const total = plan.benefitSummary?.totalBenefits || 0;
  let remaining = 0;

  category.services.forEach((service: ZuubService) => {
    const serviceRemaining = parseFloat(service.remainingBenefits) || 0;
    remaining += serviceRemaining;
  });

  return { remaining, total };
};

/**
 * Gets the selected plan based on payer name and plan type
 * If planType is null, returns the first plan with the matching payer name
 */
export const getSelectedPlan = (
  zuubPatientData: ZuubPatientData | null,
  payerName: string,
  planType: string | null,
) => {
  if (!zuubPatientData?.detailedData) {
    return null;
  }

  // Filter plans by payer name
  const matchingPlans = zuubPatientData.detailedData.filter(
    (plan) => plan.payerName === payerName,
  );

  if (matchingPlans.length === 0) {
    return null;
  }

  // If planType is provided, try to find exact match first
  if (planType) {
    const exactMatch = matchingPlans.find(
      (plan) => plan.plan?.planType === planType,
    );
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Prefer primary plan (order=0) if available, otherwise return the first match
  // Since detailedData is ZuubPatientDetailDataV3[], all plans have order property
  const primaryPlan = matchingPlans.find(
    (plan) => (plan as ZuubPatientDetailDataV3).order === 0,
  );
  if (primaryPlan) {
    return primaryPlan;
  }

  // Fall back to first matching plan
  return matchingPlans[0];
};

/**
 * Calculates summary metrics and filters appointments for table display
 * This function processes appointments to:
 * 1. Count verification statuses for SummaryCards (pending, failed, verified)
 * 2. Filter appointments for the table based on the selected card
 *
 * @param appointments - Array of appointments to process
 * @param selectedCard - The currently selected card filter ('all', 'pending', 'failed', 'verified')
 * @param zuubDataMap - Map of unifiedAppointmentId to ZuubPatientData for status lookup
 * @returns Object containing summaryMetrics and filteredAppointmentsForTable
 */
export const calculateSummaryMetricsAndFilterAppointments = (
  appointments: Appointment[] | undefined,
  selectedCard: string,
  zuubDataMap: Map<string, ZuubPatientData>,
): {
  summaryMetrics: {
    total: number;
    pending: number;
    failed: number;
    verified: number;
  };
  filteredAppointmentsForTable: Appointment[];
} => {
  if (!appointments) {
    return {
      summaryMetrics: { total: 0, pending: 0, failed: 0, verified: 0 },
      filteredAppointmentsForTable: [],
    };
  }

  const result = appointments.reduce(
    (acc, appointment) => {
      // Get the verification status for this appointment
      // This checks Zuub data first, then falls back to 'unknown' status
      const status = getAppointmentVerificationStatus(appointment, zuubDataMap);

      // Count verification statuses for SummaryCards
      // These counts will be displayed on the cards (e.g., "3 Pending", "1 Failed")
      if (status === 'pending') acc.metrics.pending++;
      else if (status === 'failed') acc.metrics.failed++;
      else if (status === 'verified') acc.metrics.verified++;

      // Filter appointments for the table based on selected card
      // If "All" is selected, include all appointments
      // If a specific status is selected (e.g., "Pending"), only include those
      if (selectedCard === 'all' || status === selectedCard) {
        acc.filteredForTable.push(appointment);
      }

      return acc;
    },
    {
      metrics: {
        total: appointments.length,
        pending: 0,
        failed: 0,
        verified: 0,
      },
      filteredForTable: [] as Appointment[],
    },
  );

  return {
    summaryMetrics: result.metrics, // For SummaryCards display
    filteredAppointmentsForTable: result.filteredForTable, // For table display
  };
};

// Type for table rows using lightweight data from the v3 list endpoint
// Each row represents one appointment with all its plans grouped together
export type AutoVerifyTableRow = {
  plans: ZuubPlanListDataV3[];
  appointment: Appointment | null;
  zuubPatient: ZuubPatientListItemV3;
};

/**
 * Maps insuranceStatusV2 to summary card categories
 * - ACTIVE -> active
 * - INACTIVE, NOT_ELIGIBLE -> noCoverage
 * - CONFIRM_PATIENT_DETAILS, CONFIRM_NPI_NUMBER, NO_PAYER_LISTED, CHECK_VIDEA_LOGIN -> confirmDetails
 * - CHECK_MANUALLY -> checkManually
 */
export const getCardCategoryFromStatus = (
  insuranceStatusV2: string | undefined,
): 'active' | 'noCoverage' | 'confirmDetails' | 'checkManually' => {
  switch (insuranceStatusV2) {
    case 'ACTIVE':
      return 'active';
    case 'INACTIVE':
    case 'NOT_ELIGIBLE':
      return 'noCoverage';
    case 'CONFIRM_PATIENT_DETAILS':
    case 'CONFIRM_NPI_NUMBER':
    case 'NO_PAYER_LISTED':
    case 'CHECK_VIDEA_LOGIN':
      return 'confirmDetails';
    case 'CHECK_MANUALLY':
    default:
      return 'checkManually';
  }
};

/**
 * Calculates summary metrics from rows based on insuranceStatusV2
 * Categories match the design:
 * - active: ACTIVE status
 * - noCoverage: INACTIVE or NOT_ELIGIBLE
 * - confirmDetails: CONFIRM_PATIENT_DETAILS, CONFIRM_NPI_NUMBER, NO_PAYER_LISTED, CHECK_VIDEA_LOGIN
 * - checkManually: CHECK_MANUALLY or unknown
 *
 * @param rows - Array of table rows to process
 * @returns Summary metrics object
 */
export const calculateSummaryMetricsFromRows = (
  rows: AutoVerifyTableRow[],
): {
  total: number;
  active: number;
  noCoverage: number;
  confirmDetails: number;
  checkManually: number;
} => {
  const metrics = {
    total: rows.length,
    active: 0,
    noCoverage: 0,
    confirmDetails: 0,
    checkManually: 0,
  };

  // Count based on primary plan (order 0) or first plan
  rows.forEach((row) => {
    const primaryPlan = row.plans.find((p) => p.order === 0) || row.plans[0];
    if (primaryPlan) {
      const category = getCardCategoryFromStatus(
        primaryPlan.insuranceStatusV2 ?? undefined,
      );
      metrics[category]++;
    }
  });

  return metrics;
};

/**
 * Filters rows based on the selected card (using insuranceStatusV2)
 *
 * @param rows - Array of table rows to filter
 * @param selectedCard - The currently selected card filter (null = show all, or 'active', 'noCoverage', 'confirmDetails', 'checkManually')
 * @returns Filtered array of rows
 */
export const filterRowsByCard = (
  rows: AutoVerifyTableRow[],
  selectedCard:
    | 'all'
    | 'active'
    | 'noCoverage'
    | 'confirmDetails'
    | 'checkManually',
): AutoVerifyTableRow[] => {
  if (selectedCard === 'all') {
    return rows;
  }

  // Filter based on primary plan (order 0) or first plan
  return rows.filter((row) => {
    const primaryPlan = row.plans.find((p) => p.order === 0) || row.plans[0];
    if (!primaryPlan) return false;
    const category = getCardCategoryFromStatus(
      primaryPlan.insuranceStatusV2 ?? undefined,
    );
    return category === selectedCard;
  });
};

/**
 * Filters rows based on selected payers
 *
 * @param rows - Array of table rows to filter
 * @param selectedPayers - Array of selected payer names (empty = all)
 * @returns Filtered array of rows
 */
export const filterRowsByPayer = (
  rows: AutoVerifyTableRow[],
  selectedPayers: string[],
): AutoVerifyTableRow[] => {
  if (selectedPayers.length === 0) {
    return rows;
  }
  // Show row if any of its plans matches the selected payers
  return rows.filter((row) => {
    return row.plans.some((plan) => {
      const payerName = plan.payerName || '';
      return selectedPayers.includes(payerName);
    });
  });
};

/**
 * Extracts unique payer names from rows for the multi-select filter
 *
 * @param rows - Array of table rows
 * @returns Array of unique payer names
 */
export const getUniquePayersFromRows = (
  rows: AutoVerifyTableRow[],
): string[] => {
  const payers = rows
    .flatMap((row) => row.plans.map((plan) => plan.payerName))
    .filter((name): name is string => !!name);
  return [...new Set(payers)].sort();
};

/**
 * Extracts unique insurance statuses from rows for the multi-select filter
 *
 * @param rows - Array of table rows
 * @returns Array of unique insurance status values
 */
export const getUniqueInsuranceStatusesFromRows = (
  rows: AutoVerifyTableRow[],
): string[] => {
  const statuses = rows
    .flatMap((row) => row.plans.map((plan) => plan.insuranceStatusV2))
    .filter((status): status is NonNullable<typeof status> => !!status);
  return [...new Set(statuses as string[])].sort();
};
